import {useEffect, useRef} from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import {membersSearchRetrieve} from 'api/v2';
import {AutocompleteSearch, Box, Skeleton} from '@wellstone-solutions/web';
import {markLabelRequiredSx} from '../NoteForm';
import type {MemberRef} from '../types';

// Notes are about patients; the server filters so staff can't crowd patients out of its 10 results
const searchMemberRefs = async (search: string): Promise<MemberRef[]> =>
  (await membersSearchRetrieve({search, role: ['patient']})).data.results.map(
    ({id, name}) => ({id, name}),
  );

type MemberFieldProps = {
  value: MemberRef | null;
  onChange: (member: MemberRef | null) => void;
  isLoading?: boolean;
  autoFocus?: boolean;
};

export const MemberField = ({
  value,
  onChange,
  isLoading = false,
  autoFocus = false,
}: MemberFieldProps) => {
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const hasFocused = useRef(false);
  // Touch devices would pop the on-screen keyboard on page load
  const hasFinePointer = useMediaQuery('(pointer: fine)', {noSsr: true});

  // AutocompleteSearch can't forward `required`, so set it on its input for screen readers
  useEffect(() => {
    inputWrapperRef.current
      ?.querySelector('input')
      ?.setAttribute('aria-required', 'true');
  }, [isLoading]);

  useEffect(() => {
    if (autoFocus && hasFinePointer && !isLoading && !hasFocused.current) {
      hasFocused.current = true;
      inputWrapperRef.current?.querySelector('input')?.focus();
    }
  }, [autoFocus, hasFinePointer, isLoading]);

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={56}
        data-testid="member-field-loading"
      />
    );
  }

  return (
    <Box sx={markLabelRequiredSx}>
      {/* web's Box doesn't forward refs */}
      <div ref={inputWrapperRef}>
        <AutocompleteSearch<MemberRef>
          label="Member"
          placeholder="Search members by name"
          value={value}
          optionLabelKey="name"
          optionValueKey="id"
          onOptionSelect={onChange}
          onSearch={searchMemberRefs}
          testId="member-field"
        />
      </div>
    </Box>
  );
};
